import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { ListBulletIcon } from "@radix-ui/react-icons";
import * as React from "react";

import { Feed } from "@/lib/types";
import { cn } from "@/lib/utils";

const Avatar = ({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) => (
  <AvatarPrimitive.Root
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full border border-slate-dim",
      className,
    )}
    {...props}
  />
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = ({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) => (
  <AvatarPrimitive.Image
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
);
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = ({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) => (
  <AvatarPrimitive.Fallback
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-slate-3 dark:bg-slatedark-3",
      className,
    )}
    {...props}
  />
);
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

function SimpleAvatar({
  src,
  alt,
  className,
  fallback,
  size = "md",
  variant = "default",
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  src: string | undefined;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "default" | "error";
}) {
  return (
    <Avatar
      {...props}
      className={cn(
        size === "xs" && "w-6 h-6",
        size === "sm" && "w-8 h-8",
        size === "md" && "w-9 h-9",
        size === "lg" && "w-12 h-12",
        className,
      )}
    >
      <AvatarImage src={src} alt={alt} />
      <AvatarFallback
        className={cn(
          variant === "error" && "bg-red-10 dark:bg-reddark-10",
          variant === "error" && "text-red-1 dark:text-reddark-1",
        )}
      >
        {fallback || alt[0]}
      </AvatarFallback>
    </Avatar>
  );
}

function FeedAvatar({
  feed,
  className,
  style,
}: {
  feed: Pick<Feed, "avatar_url" | "display_name">;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <Avatar className={cn("rounded-lg", className)}>
      <AvatarImage
        src={feed.avatar_url}
        alt={`${feed.display_name}'s avatar`}
        style={style}
      />
      <AvatarFallback className="rounded-lg">
        <div className="h-full w-full box-content p-2">
          <ListBulletIcon className="h-full w-full" />
        </div>
      </AvatarFallback>
    </Avatar>
  );
}

export { SimpleAvatar as Avatar, FeedAvatar };
